import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { ApiError, exportConfigurationSnapshot, importConfigurationSnapshot } from '@/api/client'
import type { ConfigImportSummary } from '@/api/types'
import { Drawer } from '@/components/Drawer'
import { useToast } from '@/components/toast-context'

import { ArrowDownUp } from 'lucide-react'
import { AdminSection } from '@/features/admin/AdminSection'

export function ConfigTransferAdminPage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)

  function clearPendingFile() {
    setConfirmOpen(false)
    setPendingFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const exportMut = useMutation({
    mutationFn: exportConfigurationSnapshot,
    onSuccess: () => {
      setError(null)
      toast.success('Configuration exported — check your downloads.')
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Export failed')
    },
  })

  const importMut = useMutation({
    mutationFn: importConfigurationSnapshot,
    onSuccess: async (summary: ConfigImportSummary) => {
      setError(null)
      clearPendingFile()
      // Drop cached catalogue so admin screens / year-view pickers / forms
      // pick up the imported stages, views, and settings immediately.
      await queryClient.invalidateQueries({ queryKey: ['config'] })
      await queryClient.invalidateQueries({ queryKey: ['stages'] })
      await queryClient.invalidateQueries({ queryKey: ['lookups'] })
      const created =
        summary.stages_created +
        summary.forecast_views_created +
        summary.lookup_values_created +
        summary.revenue_rules_appended
      const updated =
        summary.stages_updated + summary.forecast_views_updated + summary.lookup_values_updated
      toast.success(`Import complete — ${created} created, ${updated} updated.`)
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Import failed')
    },
  })

  async function onFileChosen(file: File | undefined) {
    setError(null)
    if (!file) return
    setPendingFile(file)
    setConfirmOpen(true)
  }

  async function confirmImport() {
    if (!pendingFile) return
    const text = await pendingFile.text()
    let doc: unknown
    try {
      doc = JSON.parse(text)
    } catch {
      setError('Selected file is not valid JSON.')
      clearPendingFile()
      return
    }
    importMut.mutate(doc as Record<string, unknown>)
  }

  return (
    <AdminSection
      title="Configuration transfer"
      tone="slate"
      icon={ArrowDownUp}
      lede="Export the full catalogue snapshot for another environment, or import a validated JSON file. Opportunity data is never changed by import."
    >
      {error && (
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      )}

      <div className="admin-panels">
        <article className="admin-panel admin-panel--export">
          <h3 className="admin-panel__title">Export</h3>
          <p className="admin-panel__note">
            Download the current catalogue as JSON for another environment.
          </p>
          <button
            type="button"
            className="button button--primary"
            disabled={exportMut.isPending}
            onClick={() => exportMut.mutate()}
          >
            {exportMut.isPending ? 'Exporting…' : 'Export configuration'}
          </button>
        </article>

        <article className="admin-panel admin-panel--import">
          <h3 className="admin-panel__title">Import</h3>
          <p className="admin-panel__note">
            Merge a validated JSON file. Opportunity data is never changed.
          </p>
          <label className="button button--secondary" style={{ cursor: 'pointer' }}>
            Choose import file
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              style={{ display: 'none' }}
              onChange={(e) => void onFileChosen(e.target.files?.[0])}
            />
          </label>
        </article>
      </div>

      <Drawer
        open={confirmOpen && pendingFile != null}
        title="Confirm import"
        onClose={clearPendingFile}
        onRequestClose={() => !importMut.isPending}
        footer={
          <div className="drawer__footer-actions">
            <button
              type="button"
              className="button button--ghost"
              disabled={importMut.isPending}
              onClick={clearPendingFile}
            >
              Cancel
            </button>
            <button
              type="button"
              className="button button--primary"
              disabled={importMut.isPending}
              onClick={() => void confirmImport()}
            >
              {importMut.isPending ? 'Importing…' : 'Import'}
            </button>
          </div>
        }
      >
        {pendingFile && (
          <p>
            Apply configuration from <strong>{pendingFile.name}</strong>? Existing catalogue rows
            will be updated or merged; feature switches change only for System administrators.
          </p>
        )}
      </Drawer>
    </AdminSection>
  )
}
